// Calendar month-grid geometry — the SINGLE source of truth shared by the
// calendar's data-fetch range (App.tsx) and its rendered month grid
// (buildCalendar in App.tsx).
//
// The month view renders a 5-6 week grid that always starts on a Sunday and
// fills whole weeks, so it includes trailing days of the PREVIOUS month (the
// leading "outside" cells) and leading days of the NEXT month (the trailing
// "outside" cells) — e.g. June 2026 shows May 31 at the top and July 1-4 at the
// bottom. The data fetch MUST cover this full displayed span (gridStart..gridEnd),
// not just the 1st..last-of-month, or those outside cells render empty and the
// user can't see events that fall on them (regression: an appointment set for
// July 1 7am never appeared in June's July-1 outside cell because that day was
// outside the fetched range). Deriving the fetch range and the rendered grid
// from this one helper means the two can never drift.
//
// This is exactly the math buildCalendar uses:
//   offset       = new Date(y, m, 1).getDay()            // 0=Sun .. 6=Sat
//   daysInMonth  = new Date(y, m + 1, 0).getDate()
//   totalCells   = Math.ceil((offset + daysInMonth) / 7) * 7   // 28 | 35 | 42
//   gridStart    = new Date(y, m, 1 - offset)            // first cell (a Sunday)
//   gridEnd      = new Date(y, m, totalCells - offset)   // last cell (a Saturday)
//
// Pure, never throws. Uses LOCAL date parts (matching the calendar's day keys),
// never UTC, so the YYYY-MM-DD strings never shift across a timezone boundary.

export interface CalendarGridBounds {
  /** First cell of the grid (a Sunday; usually in the previous month). */
  gridStart: Date
  /** Last cell of the grid (a Saturday; usually in the next month). */
  gridEnd: Date
  /** Total number of cells — always a multiple of 7 (28, 35, or 42). */
  totalCells: number
}

/**
 * Compute the displayed month-grid bounds for the month containing `month`.
 * Only the year+month of the argument matter; the day/time are ignored.
 */
export function calendarGridBounds(month: Date): CalendarGridBounds {
  const y = month.getFullYear()
  const m = month.getMonth()
  const offset = new Date(y, m, 1).getDay()
  const daysInMonth = new Date(y, m + 1, 0).getDate()
  const totalCells = Math.ceil((offset + daysInMonth) / 7) * 7
  const gridStart = new Date(y, m, 1 - offset)
  const gridEnd = new Date(y, m, totalCells - offset)
  return { gridStart, gridEnd, totalCells }
}

/**
 * Format a Date as a local-time `YYYY-MM-DD` key. Matches the calendar's
 * `toDateKey` (App.tsx) semantics — local date parts, never UTC — so the
 * fetched range bounds align exactly with the day keys the grid buckets events
 * into. Pure, never throws.
 */
export function toYmd(value: Date): string {
  const year = value.getFullYear()
  const month = `${value.getMonth() + 1}`.padStart(2, '0')
  const day = `${value.getDate()}`.padStart(2, '0')
  return `${year}-${month}-${day}`
}
